// Role-based dashboard redirection utility

export const getRoleBasedDashboard = (user) => {
  if (!user || !user.roles) {
    return '/dashboard'; // Default fallback
  }

  const userRole = getUserRole(user);

  // Redirect users/admins to My Datarooms
  if (userRole === 'user') {
    return '/datarooms';
  }

  // Super-admin goes to dashboard
  return '/dashboard';
};

export const getUserRole = (user) => {
  if (!user) {
    return null;
  }
  
  // Handle different role formats from API
  let roles = [];
  
  if (user.roles) {
    roles = Array.isArray(user.roles) ? user.roles : [user.roles];
  } else if (user.role) {
    roles = [user.role];
  }
  
  // System has only 2 roles: super-admin and user
  // Check for super-admin first
  if (roles.includes('super-admin') || roles.includes('superadmin')) {
    return 'super-admin';
  }
  
  // Everything else is treated as regular user
  if (roles.includes('admin') || roles.includes('user') || roles.length > 0) {
    return 'user';
  }
  
  return null;
};

export const isSuperAdmin = (user) => {
  if (!user) {
    return false;
  }
  
  // Handle different role formats from API
  let roles = [];
  
  if (user.roles) {
    roles = Array.isArray(user.roles) ? user.roles : [user.roles];
  } else if (user.role) {
    roles = [user.role];
  }
  
  return roles.includes('super-admin') || roles.includes('superadmin');
};

export const isUser = (user) => {
  if (!user || !user.roles) {
    return false;
  }
  
  const roles = Array.isArray(user.roles) ? user.roles : [user.roles];
  // Both 'admin' and 'user' roles are treated as regular users
  return (roles.includes('admin') || roles.includes('user')) && !roles.includes('super-admin');
};

export const getUserPermissions = (user) => {
  if (!user) {
    return [];
  }
  
  // Check for permissions in different formats
  let permissions = [];
  
  if (user.permissions) {
    permissions = Array.isArray(user.permissions) ? user.permissions : [user.permissions];
  } else if (user.permission) {
    permissions = [user.permission];
  }
  
  // Extract permission names if permissions are objects
  permissions = permissions.map(perm => {
    if (typeof perm === 'object' && perm.name) {
      return perm.name;
    }
    return perm;
  });
  
  return permissions;
};

export const hasPermission = (user, permission) => {
  const permissions = getUserPermissions(user);
  return permissions.includes(permission);
};

export const canAccessRoute = (user, route) => {
  if (!user || !user.roles) {
    return false;
  }
  
  const userRole = getUserRole(user);
  
  // Route access control based on roles
  const routePermissions = {
    // Common routes (both roles can access)
    '/dashboard': ['super-admin', 'user'],
    '/profile': ['super-admin', 'user'],
    '/my-invitations': ['super-admin', 'user'], // All users can view their invitations
    '/notifications': ['super-admin', 'user'], // All users can view notifications
    
    // Super-admin only routes
    '/users': ['super-admin'],
    '/email-templates': ['super-admin'],
    '/pricing-plans': ['super-admin'],
    '/predefined-templates': ['super-admin'],
    '/dataroom-management': ['super-admin'],
    '/features': ['super-admin'],
    '/roles': ['super-admin'],
    '/permissions': ['super-admin'],
    '/tags': ['super-admin'], // Super admin tags view
    '/admin/dataroom-roles': ['super-admin'], // Super admin dataroom roles management
    '/system-config': ['super-admin'],
    
    // User (admin role) routes
    '/subscriptions': ['user'],
    '/datarooms': ['user'],
    '/file-manager': ['user'],
    '/datarooms/:dataroomUuid/tags': ['user'], // User tags for specific dataroom
    '/datarooms/:dataroomUuid/invitations': ['super-admin', 'user'], // Both super-admin and users can manage invitations
    '/datarooms/:dataroomUuid/roles': ['super-admin', 'user'], // Both super-admin and users can manage dataroom roles
  };
  
  const allowedRoles = routePermissions[route] || [];
  return allowedRoles.includes(userRole);
};

export const getNavigationItems = (user) => {
  if (!user || !user.roles) {
    return [];
  }
  
  const userRole = getUserRole(user);
  
  // Navigation items based on role
  if (userRole === 'super-admin') {
    // Super-admin menu items
    return [
      {
        icon: 'LayoutDashboard',
        label: 'Dashboard',
        path: '/dashboard'
      },
      {
        icon: 'Users',
        label: 'Users',
        path: '/users'
      },
      {
        icon: 'FileText',
        label: 'Email Templates',
        path: '/email-templates'
      },
      {
        icon: 'DollarSign',
        label: 'Pricing Plans',
        path: '/pricing-plans'
      },
      {
        icon: 'FolderOpen',
        label: 'Dataroom Management',
        path: '/dataroom-management'
      },
      {
        icon: 'Package',
        label: 'Features',
        path: '/features'
      },
      {
        icon: 'FileText',
        label: 'Predefined Templates',
        path: '/predefined-templates'
      },
      {
        icon: 'Bell',
        label: 'Notifications',
        path: '/notifications'
      },
      {
        icon: 'Settings',
        label: 'System Config',
        path: '/system-config'
      },
      // Tags Management - Commented out as requested
      // {
      //   icon: 'Tag',
      //   label: 'Tags Management',
      //   path: '/tags'
      // },
      // Dataroom Roles - Commented out as requested
      // {
      //   icon: 'UserCheck',
      //   label: 'Dataroom Roles',
      //   path: '/admin/dataroom-roles'
      // }
    ];
  } else if (userRole === 'user') {
    // User (admin role) menu items
    // Check has_owned_dataroom flag to determine menu visibility
    const hasOwnedDataroom = user.has_owned_dataroom !== false; // Default to true if not set

    // If user doesn't have owned dataroom, show only limited menu items
    if (!hasOwnedDataroom) {
      return [
        {
          icon: 'FolderOpen',
          label: 'My Datarooms',
          path: '/datarooms'
        },
        {
          icon: 'Mail',
          label: 'My Invitations',
          path: '/my-invitations'
        },
        {
          icon: 'Bell',
          label: 'Notifications',
          path: '/notifications'
        }
      ];
    }

    // Full menu for users with owned datarooms
    return [
      {
        icon: 'LayoutDashboard',
        label: 'Dashboard',
        path: '/dashboard'
      },
      {
        icon: 'CreditCard',
        label: 'Subscriptions',
        path: '/subscriptions'
      },
      {
        icon: 'FolderOpen',
        label: 'My Datarooms',
        path: '/datarooms'
      },
      {
        icon: 'Mail',
        label: 'My Invitations',
        path: '/my-invitations'
      },
      {
        icon: 'Bell',
        label: 'Notifications',
        path: '/notifications'
      }
      // File Manager is now accessed through dataroom cards
      // Tags for users are accessed within dataroom context, not as a main menu item
    ];
  }
  
  // Return empty array for unknown roles
  return [];
};

export const getDashboardStats = (user) => {
  if (!user || !user.roles) {
    return [];
  }
  
  const userRole = getUserRole(user);
  
  // Different stats based on role
  if (userRole === 'super-admin') {
    return [
      { key: 'totalUsers', label: 'Total Users', permission: 'view users' },
      { key: 'totalAdmins', label: 'Total Admins', permission: 'view users' },
      { key: 'activeSubscriptions', label: 'Active Subscriptions', permission: 'view subscriptions' },
      { key: 'totalRevenue', label: 'Total Revenue', permission: 'view revenue' },
      { key: 'storageUsed', label: 'Storage Used', permission: 'view storage' },
      { key: 'totalDatarooms', label: 'Total Datarooms', permission: 'view datarooms' }
    ];
  }
  
  if (userRole === 'user') {
    return [
      { key: 'myDatarooms', label: 'My Datarooms', permission: 'view datarooms' },
      { key: 'activeSubscription', label: 'Active Subscription', permission: 'view subscriptions' },
      { key: 'storageUsed', label: 'Storage Used', permission: 'view storage' },
      { key: 'filesUploaded', label: 'Files Uploaded', permission: 'view files' }
    ];
  }
  
  return [];
};

// Helper function to get role display name
export const getRoleDisplayName = (role) => {
  const roleDisplayNames = {
    'super-admin': 'Super Administrator',
    'user': 'User',
    'admin': 'User' // Admin is displayed as User
  };
  
  return roleDisplayNames[role] || role;
};

// Helper function to get role badge color
export const getRoleBadgeColor = (role) => {
  const roleColors = {
    'super-admin': 'bg-purple-100 text-purple-800',
    'user': 'bg-blue-100 text-blue-800',
    'admin': 'bg-blue-100 text-blue-800' // Admin uses same color as user
  };
  
  return roleColors[role] || 'bg-gray-100 text-gray-800';
};

export default {
  getRoleBasedDashboard,
  getUserRole,
  isSuperAdmin,
  isUser,
  getUserPermissions,
  hasPermission,
  canAccessRoute,
  getNavigationItems,
  getDashboardStats,
  getRoleDisplayName,
  getRoleBadgeColor
};